import { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Image,
  Badge,
  Spinner,
  Divider,
  Alert,
  AlertIcon,
  Button,
  Input,
  Textarea,
  useToast,
  Flex,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
} from '@chakra-ui/react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';

// Dev-only affordances (editable order id to test id mismatches between the two
// systems). Baked out of the production build so customers never see it.
const IS_DEV = process.env.NODE_ENV === 'development';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Platform default timezone — used only when the tenant's timezone isn't
// available on the tracking payload. Matches the backend default.
const DEFAULT_TZ = 'America/Chicago';

/**
 * Format a UTC date string into "Jun 24, 2:30 PM" in the TENANT's timezone.
 * Passing an explicit timeZone (instead of the browser's) keeps the displayed
 * time consistent across devices and page reloads — fixes the UTC/Central flip
 * where the same order time showed 12:06 PM then 5:06 PM after a refresh.
 */
function formatFriendlyDate(dateStr, timeZone = DEFAULT_TZ) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const tz = timeZone || DEFAULT_TZ;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: tz,
  }) + ', ' + d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: tz,
  });
}

/**
 * StatusTimeline — horizontal stepper showing order progress.
 */
function StatusTimeline({ hasIntake, hasFold }) {
  const steps = [
    { label: 'Received', complete: hasIntake },
    { label: 'Processing', complete: hasIntake && !hasFold, active: hasIntake && !hasFold },
    { label: 'Ready', complete: hasFold },
  ];

  return (
    <Flex align="center" justify="center" w="100%" px={2} py={3}>
      {steps.map((step, i) => (
        <Flex key={i} align="center" flex={i < steps.length - 1 ? 1 : 'none'}>
          {/* Step circle */}
          <VStack spacing={1}>
            <Flex
              w="32px"
              h="32px"
              borderRadius="full"
              align="center"
              justify="center"
              bg={step.complete ? 'blue.500' : 'transparent'}
              border={step.active ? '3px solid' : step.complete ? 'none' : '2px solid'}
              borderColor={step.active ? 'blue.400' : step.complete ? 'blue.500' : 'gray.300'}
              color={step.complete ? 'white' : 'gray.400'}
              fontSize="sm"
              fontWeight="bold"
              animation={step.active ? 'pulse 2s infinite' : undefined}
              sx={step.active ? {
                '@keyframes pulse': {
                  '0%, 100%': { boxShadow: '0 0 0 0 rgba(66, 153, 225, 0.4)' },
                  '50%': { boxShadow: '0 0 0 8px rgba(66, 153, 225, 0)' },
                },
              } : undefined}
            >
              {step.complete ? '✓' : (i + 1)}
            </Flex>
            <Text fontSize="xs" color={step.complete || step.active ? 'blue.600' : 'gray.400'} fontWeight={step.complete || step.active ? '600' : '400'}>
              {step.label}
            </Text>
          </VStack>

          {/* Connecting line */}
          {i < steps.length - 1 && (
            <Box
              flex={1}
              h="3px"
              bg={steps[i + 1].complete || steps[i + 1].active ? 'blue.300' : 'gray.200'}
              mx={2}
              borderRadius="full"
              mt="-18px"
            />
          )}
        </Flex>
      ))}
    </Flex>
  );
}

/**
 * AdvancedPhaseCard — renders one phase (before/after) of the premium Advanced
 * AI Garment Tracking: total count, category summary, and a photo grid. All
 * photos are HTTPS presigned URLs served by our API.
 */
function AdvancedPhaseCard({ label, colorScheme, phase, onViewPhotos }) {
  const summary = Array.isArray(phase.summary) ? phase.summary : [];
  const photos = Array.isArray(phase.photos) ? phase.photos : [];
  return (
    <Box bg="white" p={4} borderRadius="lg" boxShadow="sm">
      <VStack spacing={3} align="stretch">
        <HStack justify="space-between">
          <Badge colorScheme={colorScheme} alignSelf="start" fontSize="xs">{label}</Badge>
          {phase.garmentCount != null && (
            <Text fontSize="sm" fontWeight="bold">
              {phase.garmentCount} item{phase.garmentCount !== 1 ? 's' : ''}
            </Text>
          )}
        </HStack>

        {summary.length > 0 && (
          <>
            <Divider />
            <VStack spacing={1} align="stretch">
              {summary.map((c, i) => (
                <Box key={i} display="flex" justifyContent="space-between">
                  <Text fontSize="sm">{c.name}</Text>
                  <Text fontSize="sm" fontWeight="bold">{c.count}</Text>
                </Box>
              ))}
            </VStack>
          </>
        )}

        {photos.length > 0 && (
          <Button
            size="sm"
            variant="link"
            colorScheme={colorScheme}
            alignSelf="start"
            onClick={() => onViewPhotos(label, photos)}
          >
            View {photos.length} photo{photos.length !== 1 ? 's' : ''}
          </Button>
        )}
      </VStack>
    </Box>
  );
}

/** Customer-friendly wording for orders.payment_status. */
export function describePayment(paymentStatus, balanceDue) {
  const due = parseFloat(balanceDue) > 0;
  switch (paymentStatus) {
    case 'Paid':
      return { label: 'Paid', color: 'green' };
    case 'Failed':
      return {
        label: 'Payment needed',
        color: 'red',
        message: "We couldn't charge your card on file. Please update your card so we can finish your order.",
        fixCard: true,
      };
    case 'Pending':
      return { label: 'Payment processing', color: 'blue', message: 'Your payment is being processed.' };
    case 'Hold':
      return {
        label: 'Card on file',
        color: 'blue',
        message: 'Your card is on file and will be charged when your order is ready.',
      };
    case 'Invoice Sent':
      return { label: 'Invoice sent', color: 'purple' };
    default:
      return { label: due ? 'Payment due' : 'Not yet charged', color: 'orange' };
  }
}

/**
 * Customer-facing page to view their order's item tracking photos and counts.
 * Accessed via link in the SMS notification.
 * URL: /order-tracking/:orderId?laundryId=X
 */
function OrderTrackingPhotos() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const laundryId = searchParams.get('laundryId') || '1';
  const shareToken = searchParams.get('t') || '';
  const step = (searchParams.get('step') || '').toLowerCase();
  const navigate = useNavigate();
  const customerHeaders = () => {
    const idToken = localStorage.getItem('idToken');
    return idToken ? { 'x-api-key': idToken } : {};
  };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shopInfo, setShopInfo] = useState(null);
  // Advanced AI Garment Tracking (premium) — { visible, before, after }.
  // Only populated when the tenant granted customer-photo consent AND data exists.
  const [advTracking, setAdvTracking] = useState(null);
  // Photo gallery modal (shared across before/after cards).
  const [gallery, setGallery] = useState({ title: '', photos: [] });
  const { isOpen: galleryOpen, onOpen: openGalleryModal, onClose: closeGallery } = useDisclosure();
  const openGallery = (title, photos) => { setGallery({ title, photos: photos || [] }); openGalleryModal(); };
  // Dev-only editable order id (test id mismatches between the two systems).
  const [devOrderId, setDevOrderId] = useState(orderId || '');
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackCounts, setFeedbackCounts] = useState([]);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackPhase, setFeedbackPhase] = useState('intake');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const toast = useToast();

  useEffect(() => {
    fetchTracking();
    fetchShopInfo();
    fetchAdvancedTracking();
  }, [orderId, laundryId, step]);

  const fetchAdvancedTracking = async () => {
    try {
      const res = await fetch(
        `${API_BASE}/api/advanced-tracking/customer/${orderId}?laundryId=${laundryId}${step ? `&step=${encodeURIComponent(step)}` : ''}`
      );
      if (!res.ok) return;
      const result = await res.json();
      if (result && result.visible) setAdvTracking(result);
    } catch (e) {
      // Graceful degradation — premium section just won't show.
    }
  };

  const fetchTracking = async () => {
    try {
      const params = new URLSearchParams({ laundryId });
      if (shareToken) params.set('t', shareToken);
      const res = await fetch(
        `${API_BASE}/api/track/customer/${encodeURIComponent(orderId)}?${params}`,
        { headers: customerHeaders() }
      );
      if (res.status === 403) {
        const body = await res.json().catch(() => ({}));
        setError(body.detail || 'This link has expired. Open the order from your account instead.');
        setLoading(false);
        return;
      }
      if (!res.ok) throw new Error('Could not load tracking info');
      const result = await res.json();
      setData(result);
    } catch (e) {
      setError('Unable to load order tracking details.');
    }
    setLoading(false);
  };

  const fetchShopInfo = async () => {
    try {
      const res = await fetch(
        `${API_BASE}/api/laundry/get-info?operation=getLaundryInfo&laundryId=${laundryId}&isCustomer=true`
      );
      if (!res.ok) return;
      const result = await res.json();
      if (result.status === 'success') {
        setShopInfo({ name: result.laundryName, logo: result.laundryLogo });
      }
    } catch (e) {
      // Graceful degradation — skip branding if fetch fails
    }
  };

  const handleReportIssue = (phase) => {
    const items = phase === 'intake' ? data.intakeItems : data.foldItems;
    setFeedbackPhase(phase);
    setFeedbackCounts(
      (items || []).map((item) => ({ category: item.category, count: item.count }))
    );
    setShowFeedback(true);
  };

  const handleCountChange = (index, value) => {
    const updated = [...feedbackCounts];
    updated[index] = { ...updated[index], count: parseInt(value) || 0 };
    setFeedbackCounts(updated);
  };

  const handleSubmitFeedback = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/track/customer-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...customerHeaders() },
        body: JSON.stringify({
          orderId,
          laundryId,
          phase: feedbackPhase,
          customerCounts: feedbackCounts,
          comment: feedbackComment || undefined,
          t: shareToken || undefined,
        }),
      });
      if (!res.ok) throw new Error('Failed to submit');
      setSubmitted(true);
      toast({
        title: 'Feedback submitted',
        description: "Thank you! We'll review this.",
        status: 'success',
        duration: 4000,
      });
    } catch (e) {
      toast({
        title: 'Error',
        description: 'Could not submit feedback. Please try again.',
        status: 'error',
        duration: 4000,
      });
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" p={4}
        bgGradient="linear(to-b, blue.50, gray.50)">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text>Loading your order details...</Text>
        </VStack>
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" p={4}
        bgGradient="linear(to-b, blue.50, gray.50)">
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Text>{error || 'Order not found'}</Text>
        </Alert>
      </Box>
    );
  }

  // A phase is shown when it has AI counts OR uploaded photos. When AI counting
  // is off, there are photos but no item counts — we still show the photos under
  // the clear "Received" / "Folded" labels.
  const onlyReceived = step === 'received';
  const onlyFolded = step === 'folded';
  const hasIntakeItems = !onlyFolded && data.intakeItems && data.intakeItems.length > 0;
  const hasFoldItems = !onlyReceived && data.foldItems && data.foldItems.length > 0;
  const hasIntake = hasIntakeItems || (!onlyFolded && data.intakePhotos && data.intakePhotos.length > 0);
  const hasFold = hasFoldItems || (!onlyReceived && data.foldPhotos && data.foldPhotos.length > 0);

  // Advanced (premium) tracking availability — a phase counts if it has photos
  // or a completed count. Used so the page still renders when a tenant uses ONLY
  // Advanced Tracking (no Basic intake/fold photos).
  const advBefore = advTracking?.before || {};
  const advAfter = advTracking?.after || {};
  const hasAdvBefore = !onlyFolded && advTracking?.visible && ((advBefore.photos && advBefore.photos.length > 0) || (advBefore.summary && advBefore.summary.length > 0) || advBefore.status === 'completed');
  const hasAdvAfter = !onlyReceived && advTracking?.visible && ((advAfter.photos && advAfter.photos.length > 0) || (advAfter.summary && advAfter.summary.length > 0) || advAfter.status === 'completed');
  const hasAdvanced = hasAdvBefore || hasAdvAfter;

  if (!hasIntake && !hasFold && !hasAdvanced) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" p={4}
        bgGradient="linear(to-b, blue.50, gray.50)">
        <VStack spacing={3} textAlign="center">
          <Text fontSize="2xl">📋</Text>
          <Text>
            {data.shareMode === 'off'
              ? `Order #${orderId}${data.orderStatus ? ` · ${data.orderStatus.replace(/([a-z])([A-Z])/g, '$1 $2')}` : ''}`
              : 'No item tracking recorded for this order yet.'}
          </Text>
          {data.paymentLink && (
            <Button as="a" href={data.paymentLink} colorScheme="blue">
              Pay balance{data.balanceDue ? ` $${Number(data.balanceDue).toFixed(2)}` : ''}
            </Button>
          )}
        </VStack>
      </Box>
    );
  }

  const intakeTotal = hasIntakeItems ? data.intakeItems.reduce((sum, i) => sum + (i.count || 0), 0) : 0;
  const foldTotal = hasFoldItems ? data.foldItems.reduce((sum, i) => sum + (i.count || 0), 0) : 0;
  const payment = describePayment(data.paymentStatus, data.balanceDue);

  return (
    <Box minH="100vh" bgGradient="linear(to-b, blue.50, white, gray.50)" p={4} maxW="600px" mx="auto">
      <VStack spacing={5} align="stretch">

        {/* Shop Branding Header */}
        {shopInfo && (
          <Flex
            align="center"
            justify="center"
            gap={3}
            py={3}
            px={4}
            bg="white"
            borderRadius="lg"
            boxShadow="sm"
          >
            {shopInfo.logo && (
              <Image
                src={shopInfo.logo}
                alt={shopInfo.name}
                h="36px"
                w="36px"
                objectFit="contain"
                borderRadius="md"
              />
            )}
            <Text fontSize="md" fontWeight="600" color="gray.700">
              {shopInfo.name}
            </Text>
          </Flex>
        )}

        {/* Order Heading */}
        <Box textAlign="center" pt={2}>
          <Heading size="md">
            Order{' '}
            <Box as="span" color="blue.500">#{orderId}</Box>
          </Heading>
          <Text fontSize="sm" color="gray.500">
            {onlyReceived ? 'What we received' : onlyFolded ? 'What we folded' : 'Item Tracking Details'}
          </Text>
        </Box>

        {/* DEV-ONLY: load a different order id (systems' ids may not match yet).
            Baked out of the production build. */}
        {IS_DEV && (
          <Box bg="yellow.50" p={3} borderRadius="md" border="1px dashed" borderColor="yellow.300">
            <Text fontSize="xs" color="gray.600" mb={1}>DEV: load a different order id</Text>
            <HStack>
              <Input
                size="sm"
                bg="white"
                value={devOrderId}
                onChange={(e) => setDevOrderId(e.target.value)}
                placeholder={orderId}
              />
              <Button
                size="sm"
                colorScheme="yellow"
                onClick={() => {
                  const id = (devOrderId || '').trim();
                  if (id) navigate(`/order-tracking/${encodeURIComponent(id)}?laundryId=${encodeURIComponent(laundryId)}`);
                }}
              >
                Load
              </Button>
            </HStack>
          </Box>
        )}

        {/* Status Timeline */}
        <Box bg="white" borderRadius="lg" boxShadow="sm" px={4} py={3}>
          <StatusTimeline hasIntake={hasIntake} hasFold={hasFold} />
        </Box>

        {/* Items Count Summary */}
        <Flex justify="center" gap={2} flexWrap="wrap">
          {hasIntake && (
            <Badge colorScheme="blue" px={3} py={1} borderRadius="full" fontSize="xs">
              {hasIntakeItems ? `${intakeTotal} item${intakeTotal !== 1 ? 's' : ''} received` : 'Received'}
            </Badge>
          )}
          {hasFold && (
            <Badge colorScheme="green" px={3} py={1} borderRadius="full" fontSize="xs">
              {hasFoldItems ? `${foldTotal} item${foldTotal !== 1 ? 's' : ''} folded` : 'Folded'}
            </Badge>
          )}
        </Flex>

        {/* Order Details & Payment */}
        {data.orderStatus && (
          <Box bg="white" p={4} borderRadius="lg" boxShadow="sm">
            <VStack spacing={3} align="stretch">
              <HStack justify="space-between">
                <Text fontWeight="bold" fontSize="sm">Order Details</Text>
                <Badge colorScheme={payment.color} fontSize="xs">
                  {payment.label}
                </Badge>
              </HStack>

              {payment.message && (
                <Box bg={`${payment.color}.50`} p={3} borderRadius="md" border="1px solid" borderColor={`${payment.color}.200`}>
                  <Text fontSize="sm" color={`${payment.color}.800`}>{payment.message}</Text>
                  {payment.fixCard && (
                    <Button
                      as="a"
                      href={`/${laundryId}/user/my-orders?order_id=${encodeURIComponent(orderId)}&is_open=true`}
                      colorScheme={payment.color}
                      size="sm"
                      mt={2}
                      w="full"
                      borderRadius="full"
                    >
                      Update card
                    </Button>
                  )}
                </Box>
              )}

              <Divider />

              {/* Dates */}
              <HStack justify="space-between" fontSize="xs" color="gray.600">
                {data.pickupDate && <Text>Pickup: {data.pickupDate}</Text>}
                {data.dropoffDate && <Text>Due: {data.dropoffDate}</Text>}
              </HStack>

              {/* Services */}
              {data.services && data.services.length > 0 && (
                <VStack spacing={1} align="stretch">
                  {data.services.map((svc, i) => (
                    <HStack key={i} justify="space-between" fontSize="sm">
                      <Text>{svc.service || svc.productName}</Text>
                      <Text fontWeight="600">${svc.servicePrice || svc.productPrice || '0'}</Text>
                    </HStack>
                  ))}
                </VStack>
              )}

              {/* Total */}
              {data.grandTotal && (
                <HStack justify="space-between" pt={1} borderTop="1px solid" borderColor="gray.100">
                  <Text fontWeight="bold" fontSize="sm">Total</Text>
                  <Text fontWeight="bold" fontSize="sm">${data.grandTotal}</Text>
                </HStack>
              )}

              {/* Pay Now button if unpaid */}
              {data.paymentStatus !== 'Paid' && data.paymentLink && data.balanceDue && parseFloat(data.balanceDue) > 0 && (
                <Box bg="orange.50" p={3} borderRadius="md" border="1px solid" borderColor="orange.200" mt={2}>
                  <VStack spacing={2}>
                    <Text fontSize="sm" color="orange.700" fontWeight="600">
                      Balance Due: ${data.balanceDue}
                    </Text>
                    <Button
                      as="a"
                      href={data.paymentLink}
                      colorScheme="orange"
                      size="md"
                      w="full"
                      borderRadius="full"
                    >
                      💳 Pay Now
                    </Button>
                  </VStack>
                </Box>
              )}

              {/* Paid confirmation */}
              {data.paymentStatus === 'Paid' && (
                <HStack justify="center" bg="green.50" p={2} borderRadius="md">
                  <Text fontSize="sm" color="green.600" fontWeight="600">✓ Payment Complete</Text>
                </HStack>
              )}
            </VStack>
          </Box>
        )}

        {/* Intake Section */}
        {hasIntake && (
          <Box bg="white" p={4} borderRadius="lg" boxShadow="sm">
            <VStack spacing={3} align="stretch">
              <Badge colorScheme="blue" alignSelf="start" fontSize="xs">RECEIVED</Badge>

              {data.intakePhotos && data.intakePhotos.length > 0 && (
                <Box>
                  <Image
                    src={data.intakePhotos[2] || data.intakePhotos[0]}
                    borderRadius="md"
                    h="180px"
                    w="full"
                    objectFit="cover"
                    cursor="pointer"
                    onClick={() => window.open(data.intakePhotos[2] || data.intakePhotos[0], '_blank')}
                    alt="Intake photo"
                  />
                </Box>
              )}

              {hasIntakeItems && (
                <>
                  <Divider />
                  <VStack spacing={1} align="stretch">
                    {data.intakeItems.map((item, i) => (
                      <Box key={i} display="flex" justifyContent="space-between">
                        <Text fontSize="sm">{item.category}</Text>
                        <Text fontSize="sm" fontWeight="bold">{item.count}</Text>
                      </Box>
                    ))}
                  </VStack>
                </>
              )}

              {data.intakeConfirmedAt && (
                <Text fontSize="xs" color="gray.400">
                  Counted: {formatFriendlyDate(data.intakeConfirmedAt, data.laundryTimezone)}
                </Text>
              )}
            </VStack>
          </Box>
        )}

        {/* Fold Section */}
        {hasFold && (
          <Box bg="white" p={4} borderRadius="lg" boxShadow="sm">
            <VStack spacing={3} align="stretch">
              <Badge colorScheme="green" alignSelf="start" fontSize="xs">FOLDED & READY</Badge>

              {data.foldPhotos && data.foldPhotos.length > 0 && (
                <Box>
                  <Image
                    src={data.foldPhotos[2] || data.foldPhotos[0]}
                    borderRadius="md"
                    h="180px"
                    w="full"
                    objectFit="cover"
                    cursor="pointer"
                    onClick={() => window.open(data.foldPhotos[2] || data.foldPhotos[0], '_blank')}
                    alt="Fold photo"
                  />
                </Box>
              )}

              {hasFoldItems && (
                <>
                  <Divider />
                  <VStack spacing={1} align="stretch">
                    {data.foldItems.map((item, i) => (
                      <Box key={i} display="flex" justifyContent="space-between">
                        <Text fontSize="sm">{item.category}</Text>
                        <Text fontSize="sm" fontWeight="bold">{item.count}</Text>
                      </Box>
                    ))}
                  </VStack>
                </>
              )}

              {data.foldConfirmedAt && (
                <Text fontSize="xs" color="gray.400">
                  Completed: {formatFriendlyDate(data.foldConfirmedAt, data.laundryTimezone)}
                </Text>
              )}
            </VStack>
          </Box>
        )}

        {/* Advanced AI Garment Tracking (premium) — shown only when the tenant
            granted customer-photo consent and the partner has completed data.
            Photos are HTTPS presigned (never the raw partner URL). */}
        {hasAdvanced && (hasAdvBefore || hasAdvAfter) && (
          <Box bg="white" p={4} borderRadius="lg" boxShadow="sm">
            <Text fontWeight="600" fontSize="sm">Garments</Text>
          </Box>
        )}
        {hasAdvBefore && (
          <AdvancedPhaseCard
            label="RECEIVED · BEFORE WASH"
            colorScheme="blue"
            phase={advBefore}
            onViewPhotos={openGallery}
          />
        )}
        {hasAdvAfter && (
          <AdvancedPhaseCard
            label="CLEANED · AFTER WASH"
            colorScheme="green"
            phase={advAfter}
            onViewPhotos={openGallery}
          />
        )}

        {/* Report Issue / Customer Feedback Section */}
        {!submitted ? (
          <Box bg="white" p={4} borderRadius="lg" boxShadow="sm">
            <VStack spacing={3} align="stretch">
              {!showFeedback ? (
                <VStack spacing={2} display={(hasIntakeItems || hasFoldItems) ? 'flex' : 'none'}>
                  <Text fontSize="sm" color="gray.600" textAlign="center">
                    Something doesn't look right?
                  </Text>
                  <HStack spacing={2} justify="center">
                    {hasIntakeItems && (
                      <Button
                        size="sm"
                        variant="outline"
                        colorScheme="orange"
                        onClick={() => handleReportIssue('intake')}
                      >
                        Report Intake Issue
                      </Button>
                    )}
                    {hasFoldItems && (
                      <Button
                        size="sm"
                        variant="outline"
                        colorScheme="orange"
                        onClick={() => handleReportIssue('fold')}
                      >
                        Report Fold Issue
                      </Button>
                    )}
                  </HStack>
                </VStack>
              ) : (
                <VStack spacing={3} align="stretch">
                  <Text fontSize="sm" fontWeight="bold">
                    Correct the counts ({feedbackPhase === 'intake' ? 'Received' : 'Folded'}):
                  </Text>

                  {feedbackCounts.map((item, i) => (
                    <HStack key={i} justify="space-between">
                      <Text fontSize="sm">{item.category}</Text>
                      <Input
                        size="sm"
                        type="number"
                        w="70px"
                        min={0}
                        value={item.count}
                        onChange={(e) => handleCountChange(i, e.target.value)}
                      />
                    </HStack>
                  ))}

                  <Textarea
                    placeholder="Optional note (e.g. 'I sent 6 shirts not 5')"
                    size="sm"
                    value={feedbackComment}
                    onChange={(e) => setFeedbackComment(e.target.value)}
                  />

                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      colorScheme="orange"
                      isLoading={submitting}
                      onClick={handleSubmitFeedback}
                    >
                      Submit Feedback
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowFeedback(false)}
                    >
                      Cancel
                    </Button>
                  </HStack>
                </VStack>
              )}
            </VStack>
          </Box>
        ) : (
          <Box bg="green.50" p={4} borderRadius="lg">
            <Text fontSize="sm" color="green.700" textAlign="center">
              ✓ Thank you! We'll review this.
            </Text>
          </Box>
        )}

        {/* Close tab button */}
        <Button
          variant="ghost"
          colorScheme="gray"
          size="sm"
          w="full"
          onClick={() => { try { window.close(); } catch (e) { /* ignore */ } }}
        >
          ✕ Close This Tab
        </Button>
      </VStack>

      {/* Photo gallery modal (shared by before/after cards) */}
      <Modal isOpen={galleryOpen} onClose={closeGallery} size="xl" isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody p={3}>
            <Text fontWeight="600" mb={3}>
              {gallery.title} — {gallery.photos.length} photo{gallery.photos.length !== 1 ? 's' : ''}
            </Text>
            <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
              {gallery.photos.map((ph, i) => (
                <Box key={ph.clothId ?? i}>
                  <Image
                    src={ph.photoUrl}
                    alt={ph.clothType || 'garment'}
                    borderRadius="md"
                    objectFit="cover"
                    w="100%"
                    h="140px"
                    cursor="pointer"
                    onClick={() => window.open(ph.photoUrl, '_blank')}
                  />
                  {ph.clothType && (
                    <Text fontSize="xs" color="gray.600" noOfLines={1} textAlign="center" mt={1}>
                      {ph.clothType}
                    </Text>
                  )}
                </Box>
              ))}
            </SimpleGrid>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default OrderTrackingPhotos;
